import {
  getArtifactWorkflowNodes,
  getCurrentArtifactWorkflowNode,
  projectArtifactWorkflow,
} from "./artifact-workflow";
import type {
  ArtifactWorkflow,
  ArtifactWorkflowNode,
  ArtifactWorkflowOperationPhase,
  SessionArtifactRecord,
} from "./types";

export type ArtifactSurfaceKind =
  | "input"
  | "review"
  | "executing"
  | "approval"
  | "blocked"
  | "failure"
  | "partial"
  | "result"
  | "canceled"
  | "stale";

export type ArtifactSurfaceActionId =
  | "continue"
  | "edit"
  | "execute"
  | "approve"
  | "decline"
  | "retry"
  | "refresh"
  | "check_status"
  | "close";

export interface ArtifactSurfaceAction {
  id: ArtifactSurfaceActionId;
  label: string;
  disabled: boolean;
  disabledReason: string | null;
  tone: "primary" | "secondary" | "destructive";
}

export interface ArtifactSurfaceContext {
  activeEditorNodeId?: string | null;
  busyAction?: string | null;
  canApprove?: boolean;
  canEdit?: boolean;
  canExecute?: boolean;
  mutationsDisabled?: boolean;
  directError?: string | null;
  transportStatus?: "connected" | "reconnecting" | "offline";
}

export interface ArtifactSurfaceModel {
  artifactId: string;
  revision: number;
  updatedAt: number;
  kind: ArtifactSurfaceKind;
  title: string;
  description: string | null;
  statusLabel: string | null;
  announcement: string;
  workflow: ArtifactWorkflow;
  activeNodeId: string | null;
  operationNodeId: string | null;
  showNavigation: boolean;
  primaryAction: ArtifactSurfaceAction | null;
  secondaryActions: ArtifactSurfaceAction[];
  transportStatus: "connected" | "reconnecting" | "offline";
}

function operationNode(
  workflow: ArtifactWorkflow,
): ArtifactWorkflowNode | null {
  return (
    getArtifactWorkflowNodes(workflow).find(
      (node) => node.kind === "operation",
    ) || null
  );
}

function operationStatusLabel(
  phase: ArtifactWorkflowOperationPhase | null | undefined,
): string | null {
  if (phase === "reviewing") return "Preparing";
  if (phase === "sending") return "In progress";
  if (phase === "synchronizing") return "Saving";
  if (phase === "verifying") return "Verifying";
  if (phase === "waiting_for_approval") return "Awaiting approval";
  if (phase === "completed") return "Completed";
  if (phase === "failed") return "Failed";
  return null;
}

function action(
  id: ArtifactSurfaceActionId,
  label: string,
  tone: ArtifactSurfaceAction["tone"],
  disabled: boolean,
  disabledReason: string | null = null,
): ArtifactSurfaceAction {
  return { id, label, tone, disabled, disabledReason };
}

function navigationIsUseful(workflow: ArtifactWorkflow): boolean {
  const nodes = getArtifactWorkflowNodes(workflow).filter(
    (node) => node.kind !== "operation" && node.kind !== "group",
  );
  return (
    nodes.length > 1 || nodes.some((node) => node.kind === "child_artifact")
  );
}

function inputNode(
  workflow: ArtifactWorkflow,
  activeEditorNodeId: string | null,
): ArtifactWorkflowNode | null {
  if (activeEditorNodeId) {
    const active = workflow.nodesById[activeEditorNodeId];
    if (active && (active.kind === "input" || active.kind === "relationship")) {
      return active;
    }
  }
  const current = getCurrentArtifactWorkflowNode(workflow);
  return current?.kind === "input" || current?.kind === "relationship"
    ? current
    : null;
}

export function selectArtifactSurface(
  artifact: SessionArtifactRecord,
  context: ArtifactSurfaceContext = {},
): ArtifactSurfaceModel {
  const workflow = artifact.workflow || projectArtifactWorkflow(artifact);
  const operation = operationNode(workflow);
  const workflowStatus = operation?.status || null;
  const terminal =
    workflowStatus === "completed" || workflowStatus === "canceled";
  const requestedEditorNodeId = terminal
    ? null
    : context.activeEditorNodeId || null;
  const current = getCurrentArtifactWorkflowNode(workflow);
  const activeInput = inputNode(workflow, requestedEditorNodeId);
  const mutationsDisabled = context.mutationsDisabled === true;
  const busy = Boolean(context.busyAction);
  const disabledReason = mutationsDisabled
    ? "Reconnect to continue."
    : busy
      ? "This action is already in progress."
      : null;
  const actionDisabled = Boolean(disabledReason);
  const transportStatus = context.transportStatus || "connected";

  let kind: ArtifactSurfaceKind;
  if (artifact.status === "completed" && workflowStatus === "completed") {
    kind = "result";
  } else if (artifact.status === "completed" && workflowStatus === "failed") {
    kind = "failure";
  } else if (artifact.status === "completed" && workflowStatus === "working") {
    kind = "executing";
  } else if (
    artifact.status === "completed" &&
    workflowStatus &&
    workflowStatus !== "canceled"
  ) {
    // Do not let a stale terminal flag hide unfinished plan work. The SDK
    // projection intentionally turns this impossible combination into an
    // attention surface until the authoritative plan catches up.
    kind = "blocked";
  } else if (artifact.status === "completed") kind = "result";
  else if (artifact.status === "canceled") kind = "canceled";
  else if (artifact.status === "stale") kind = "stale";
  else if (artifact.status === "partially_completed") kind = "partial";
  else if (artifact.status === "failed" || context.directError)
    kind = "failure";
  else if (artifact.status === "blocked") kind = "blocked";
  else if (artifact.status === "awaiting_human") kind = "approval";
  else if (artifact.status === "running" || busy) kind = "executing";
  else if (
    requestedEditorNodeId ||
    artifact.status === "draft" ||
    artifact.status === "needs_edit"
  ) {
    kind = "input";
  } else {
    kind = "review";
  }

  let title = artifact.label;
  let description = artifact.description || null;
  let statusLabel: string | null = null;
  let primaryAction: ArtifactSurfaceAction | null = null;
  let secondaryActions: ArtifactSurfaceAction[] = [];

  if (kind === "input") {
    title = activeInput?.label || artifact.label;
    description = activeInput?.description || artifact.description || null;
    statusLabel = activeInput?.status === "needs_input" ? "Needs input" : null;
    primaryAction = action(
      "continue",
      "Continue",
      "primary",
      actionDisabled,
      disabledReason,
    );
  } else if (kind === "review") {
    title = artifact.label;
    description = artifact.description || operation?.description || null;
    primaryAction = action(
      "execute",
      operation?.label || "Continue",
      "primary",
      actionDisabled || context.canExecute === false,
      disabledReason,
    );
    if (context.canEdit) {
      secondaryActions = [
        action(
          "edit",
          "Edit details",
          "secondary",
          actionDisabled,
          disabledReason,
        ),
      ];
    }
  } else if (kind === "executing") {
    title = operation?.operationDetail || operation?.label || artifact.label;
    description = operation?.operationDetail ? artifact.label : null;
    statusLabel =
      operationStatusLabel(operation?.operationPhase) || "In progress";
  } else if (kind === "approval") {
    title = operation?.attention?.title || operation?.label || artifact.label;
    description =
      operation?.attention?.detail || operation?.description || null;
    statusLabel = "Awaiting approval";
    if (context.canApprove) {
      primaryAction = action(
        "approve",
        "Approve",
        "primary",
        actionDisabled,
        disabledReason,
      );
      secondaryActions = [
        action(
          "decline",
          "Decline",
          "secondary",
          actionDisabled,
          disabledReason,
        ),
      ];
    }
  } else if (kind === "failure" || kind === "blocked") {
    const attention = current?.attention || operation?.attention;
    title = attention?.title || artifact.label;
    description =
      context.directError ||
      attention?.detail ||
      operation?.description ||
      null;
    statusLabel = kind === "failure" ? "Failed" : "Needs attention";
    if (attention?.retryable) {
      primaryAction = action(
        "retry",
        "Retry",
        "primary",
        actionDisabled,
        disabledReason,
      );
    } else if (context.canEdit) {
      primaryAction = action(
        "edit",
        "Edit details",
        "primary",
        actionDisabled,
        disabledReason,
      );
    }
  } else if (kind === "partial") {
    title = operation?.attention?.title || artifact.label;
    description =
      operation?.attention?.detail || operation?.description || null;
    statusLabel = "Partially completed";
    primaryAction = action(
      "retry",
      "Retry unfinished work",
      "primary",
      actionDisabled,
      disabledReason,
    );
  } else if (kind === "stale") {
    title = operation?.attention?.title || artifact.label;
    description =
      operation?.attention?.detail || operation?.description || null;
    statusLabel = "Details changed";
    primaryAction = action(
      "refresh",
      "Review latest details",
      "primary",
      actionDisabled,
      disabledReason,
    );
  } else if (kind === "result") {
    title = artifact.label;
    description = operation?.recap?.summary || artifact.description || null;
    statusLabel = "Completed";
    primaryAction = action("close", "Done", "primary", false);
  } else {
    title = artifact.label;
    description = operation?.attention?.detail || "Nothing else will run.";
    statusLabel = "Canceled";
    primaryAction = action("close", "Done", "primary", false);
  }

  const activeNodeId =
    kind === "input"
      ? activeInput?.id || workflow.currentNodeId
      : workflow.currentNodeId;
  const announcement = [statusLabel, title, description]
    .filter((value): value is string => Boolean(value))
    .join(". ");

  return {
    artifactId: artifact.artifactId,
    revision: workflow.revision,
    updatedAt: artifact.updatedAt,
    kind,
    title,
    description,
    statusLabel,
    announcement,
    workflow,
    activeNodeId,
    operationNodeId: operation?.id || null,
    showNavigation: navigationIsUseful(workflow),
    primaryAction,
    secondaryActions,
    transportStatus,
  };
}
